/* istanbul ignore file */

import { Component, ElementRef, EmbeddedViewRef, inject, Injector, ViewContainerRef, DOCUMENT, input, effect } from '@angular/core';

import { ApContentDSLService } from './dsl/content-dsl.service';
import { CONTENT_COMPONENTS } from './content.model';
import { ApContentService } from './content.service';

/**
 * @ngdoc component
 * @name ApContentComponent
 * @module ApContentModule
 */
@Component({
    selector: 'ap-content',
    template: '',
    providers: [ApContentDSLService]
})
export class ApContentComponent {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly contentService = inject(ApContentService);
    private readonly contentDslService = inject(ApContentDSLService);
    private readonly injector = inject(Injector);
    private readonly element = inject(ElementRef);

    private readonly viewContainerRef = inject(ViewContainerRef);

    /**
     * Content key to render
     */
    public readonly key = input.required<string>();

    /**
     * Params to pass to content key
     */
    public readonly params = input<{
        [key: string]: string;
    }>();

    private readonly dslPlaceholderNodeString = 'ap-content-dsl-placeholder';
    private readonly dslPlaceholderHtmlString = `<${this.dslPlaceholderNodeString}></${this.dslPlaceholderNodeString}>`;
    private readonly dslItems: { [key: string]: any };

    public constructor() {
        const injectedDslItems = inject(CONTENT_COMPONENTS, { optional: true });

        this.dslItems = injectedDslItems?.reduce((p, c) => ({ ...p, ...c }), {});

        effect(() => this.injectContentEffect());
    }

    private injectContentEffect(): void {
        Array.from(this.element.nativeElement.childNodes).forEach((node: HTMLElement) => node.parentNode.removeChild(node));
        const contentString = this.contentService.get(this.key(), this.params());
        contentString && this.element.nativeElement.appendChild(this.injectContent(contentString, this.injector));
    }

    private injectContent(contentString: string, injector?: Injector): HTMLElement {
        const initialParent = this.document.createElement('div');
        const dslElements = this.contentDslService.parseDSLStrings(contentString);
        let formattedContentString = contentString;
        dslElements.forEach(
            (dslElement) => (formattedContentString = formattedContentString.replace(`[[${dslElement}]]`, this.dslPlaceholderHtmlString))
        );
        initialParent.innerHTML = formattedContentString;
        const placeholderElements = initialParent.querySelectorAll(this.dslPlaceholderNodeString);

        dslElements &&
            dslElements.forEach((value, index) => {
                this.addComponent(value, placeholderElements.item(index) as HTMLElement, injector);
            });
        return initialParent;
    }

    private addComponent(value: string, oldNode: HTMLElement, injector?: Injector): void {
        const componentOutline = this.contentDslService.createComponentOutline(value);

        if (!this.dslItems || !this.dslItems[componentOutline.componentName]) {
            oldNode.parentNode.removeChild(oldNode);
            return;
        }
        this.viewContainerRef.createComponent(ApTemplateComponent, { injector });
        const childInjector = Injector.create({
            providers: []
        });
        const projectedNodes = Array.from(this.injectContent(componentOutline.componentProjectedHtml, childInjector).childNodes);
        // We create the component and then use its injector as a parent for a child injector
        // This way we can guarantee the injector hierarchy when a child depends on a componentProvider of its parent
        const componentRef = this.viewContainerRef.createComponent(this.dslItems[componentOutline.componentName], {
            injector,
            projectableNodes: [projectedNodes]
        });

        const componentElement = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
        for (const key in componentOutline.componentInputs) {
            if (componentOutline.componentInputs.hasOwnProperty(key)) {
                componentRef.setInput(key, componentOutline.componentInputs[key]);
            }
        }

        oldNode.parentNode.replaceChild(componentElement, oldNode);
    }
}

// Template class to be used to append nodes on.
// Should not be used outside of this class, thus is not exported.
@Component({
    template: ''
})
class ApTemplateComponent {}
